import { NetworkStatus, useReactiveVar } from "@apollo/client";
import { useLocalSearchParams } from "expo-router";
import { RefreshCwIcon } from "lucide-react-native";
import {
  ReactElement,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { ListRenderItem } from "@/components/atoms/FlashList";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import Header from "@/features/similar-players/components/organisms/Header";
import ListEmpty from "@/features/similar-players/components/molecules/ListEmpty";
import LoadingSkeleton from "@/features/similar-players/components/organisms/LoadingSkeleton";
import {
  DEFAULT_MAX_AGE,
  DEFAULT_MAX_COST,
  DEFAULT_MAX_MONTHS_REMAINING,
  DEFAULT_MIN_AGE,
  DEFAULT_MIN_COST,
  DEFAULT_MIN_MONTHS_REMAINING,
  filterValuesVar,
} from "../../stores/filterValues";
import MainView from "./MainView";

import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import SearchResultsItem from "@/components/organisms/SearchResultsItem";
import SkeletonList from "@/components/organisms/SkeletonList";
import { AnalyticsTracker } from "@/features/analytics";
import { spacingForDevicesWithoutNotch } from "@/config/constants";
import PlayerDetailErrorState from "@/features/player-detail/components/molecules/PlayerDetailErrorState";
import type { SimilarPlayersListState } from "@/features/player-detail/types";
import { deriveSimilarPlayersListState } from "@/features/player-detail/utils/similarPlayersQueryState";
import {
  PlayerSearchPlayerFragment,
  SimilarPlayerSortField,
  SimilarPlayersPlayerResultFragment,
  SortOrder,
  useSimilarPlayersListSimilarPlayersQuery,
} from "@/generated/graphql";
import useCreateWatchlistActions from "@/hooks/useCreateWatchlistActions";
import useMobileScreen from "@/hooks/useMobileScreen";
import { usePlayerDetailQuery } from "@/hooks/usePlayerDetailQuery";
import useSetPageTitle from "@/hooks/useSetPageTitle";
import { useCanAccessFeature } from "@/providers/FeatureFlags";
import { useTheme } from "@/providers/ThemeProvider";
import { similaritySortVar } from "@/stores/selectionSort";

/** Copy for the failed list state (SE-7596, lifted from PlayerDetail SimilarPlayersList/SE-7580). */
const LIST_ERROR_TITLE = "Could not load similar players";
/**
 * Subtitle for the failed state. Naming that a failed read is NOT the same
 * as an empty one is load-bearing -- it is the whole reason this screen
 * stopped rendering the "No Similar Players" copy for a failed query.
 */
const LIST_ERROR_SUBTITLE =
  "We could not load the similar players for this player. That is not the same as there being none -- try again.";
/** Copy for the stale list state (results shown, refresh failed). */
const LIST_STALE_MESSAGE =
  "These results may be out of date -- the last refresh failed.";

/**
 * Renders the FlashList empty slot for the given read state (SE-7596).
 *
 * FlashList only invokes this when data is empty, which happens for the
 * loading, empty, and failed states -- never stale/ready, both of which
 * carry players by construction (deriveSimilarPlayersListState). The
 * exhaustive switch still requires a branch for every member so a state
 * added later fails to compile here instead of silently reusing the
 * "No Similar Players" copy for an outcome that never earned it.
 * @param args - The state, refresh flag, and retry handler.
 * @param args.listState - The state the similar-players read resolved to.
 * @param args.isRefreshing - Whether a retry is already in flight.
 * @param args.onRetry - Retries the read.
 * @returns The element FlashList should render in its empty slot.
 */
const renderListEmptyComponent = ({
  listState,
  isRefreshing,
  onRetry,
}: {
  listState: SimilarPlayersListState;
  isRefreshing: boolean;
  onRetry: () => void;
}): ReactElement | null => {
  switch (listState.kind) {
    case "loading":
      return <SkeletonList type="player" />;
    case "failed":
      return (
        <PlayerDetailErrorState
          title={LIST_ERROR_TITLE}
          subtitle={LIST_ERROR_SUBTITLE}
          onRetry={onRetry}
          isRetrying={isRefreshing}
        />
      );
    case "empty":
      return <ListEmpty />;
    case "stale":
    case "ready":
      return null;
    default: {
      const exhaustive: never = listState;
      return exhaustive;
    }
  }
};

/**
 * Renders the staleness banner above the list when the read is stale
 * (SE-7596): cached rows are on screen but the last refresh failed. Mirrors
 * the banner SE-7580 added to SimilarPlayersListView so the disclosure reads
 * the same way across the tab and this standalone screen.
 * @param listState - The read state to check.
 * @param isRefreshing - Whether a retry is already in flight.
 * @param onRetry - Retries the read.
 * @returns The banner element, or null outside the stale state.
 */
const renderStaleBanner = (
  listState: SimilarPlayersListState,
  isRefreshing: boolean,
  onRetry: () => void
): ReactElement | null =>
  listState.kind === "stale" ? (
    <Box accessibilityRole="alert">
      <Stack
        direction="horizontal"
        className="items-center justify-between gap-2 border-b border-outline-subtle bg-status-warning-surface px-4 py-2"
      >
        <Text variant="caption" className="shrink text-status-warning-strong">
          {LIST_STALE_MESSAGE}
        </Text>
        <Button
          label="Retry"
          tone="secondary"
          variant="outline"
          size="sm"
          labelTone="secondary"
          labelVariant="caption"
          icon={RefreshCwIcon}
          iconTone="secondary"
          isLoading={isRefreshing}
          isDisabled={isRefreshing}
          onPress={onRetry}
        />
      </Stack>
    </Box>
  ) : null;

/**
 * Main container for the similar players feature, managing data fetching, filtering, and user interactions.
 */
const MainContainer = () => {
  const { bottom } = useSafeAreaInsets();
  const { playerId } = useLocalSearchParams<{ playerId: string }>();
  const filterVars = useReactiveVar(filterValuesVar);
  const similaritySortSetting = useReactiveVar(similaritySortVar);
  const isSortPlayerEnable = useCanAccessFeature("globalSort");
  const [valueAccordion, setValueAccordion] = useState<string[]>([]);
  const [showActionSheet, setShowActionSheet] = useState<boolean>(false);
  const promptRef = useRef<CustomPromptRefProps>(
    null
  ) as RefObject<CustomPromptRefProps>;
  const [playerDetail, setPlayerDetail] = useState<
    PlayerSearchPlayerFragment | undefined
  >(undefined);
  const { handleAddWatchlistPress } = useCreateWatchlistActions({
    promptRefForModal: promptRef,
  });
  const handleAddPlayer = useCallback(() => setShowActionSheet(true), []);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const handleClose = useCallback(() => setShowActionSheet(false), []);
  const isSmallScreen = useMobileScreen();
  const { data: playerData, loading: playerLoading } = usePlayerDetailQuery({
    variables: { id: playerId },
    notifyOnNetworkStatusChange: true,
    skip: !playerId,
  });
  // Compute sort values - ensure we use SortOrder enum for API compatibility
  const sortByValue =
    isSortPlayerEnable && similaritySortSetting?.sortOrder
      ? (similaritySortSetting.sortBy as SimilarPlayerSortField)
      : undefined;
  const sortOrderValue =
    isSortPlayerEnable && similaritySortSetting?.sortOrder
      ? similaritySortSetting.sortOrder === "ASC"
        ? SortOrder.Asc
        : SortOrder.Desc
      : undefined;

  // Memoize query variables so we can pass them to refetch and fetchMore
  const queryVariables = useMemo(() => {
    const filters = filterVars ?? {};
    return {
      playerId,
      filter: {
        leagueIds: filters.leagues?.map(l => l.id) ?? undefined,
        nationalityIds: filters.nationalities?.map(n => n.id) ?? undefined,
        minAge: filters.minAge ?? DEFAULT_MIN_AGE,
        maxAge: filters.maxAge ?? DEFAULT_MAX_AGE,
        // SE-6496: the Cost filter binds the GPV midpoint, not the legacy
        // Transfermarkt valuation. An active bound excludes players without a
        // GPV, so the bounds are omitted until the user sets a cost filter --
        // an untouched filter must leave results unchanged.
        ...(filters.minSalary != null || filters.maxSalary != null
          ? {
              minGeminiPlayerValuation: filters.minSalary ?? DEFAULT_MIN_COST,
              maxGeminiPlayerValuation: filters.maxSalary ?? DEFAULT_MAX_COST,
            }
          : {}),
        minMonthsRemaining:
          filters.minMonthsRemaining ?? DEFAULT_MIN_MONTHS_REMAINING,
        maxMonthsRemaining:
          filters.maxMonthsRemaining ?? DEFAULT_MAX_MONTHS_REMAINING,
        sortBy: sortByValue,
        sortOrder: sortOrderValue,
      },
    };
  }, [playerId, filterVars, sortByValue, sortOrderValue]);

  const {
    data,
    loading: allDataLoading,
    error: similarPlayersError,
    fetchMore,
    refetch,
    networkStatus,
  } = useSimilarPlayersListSimilarPlayersQuery({
    variables: queryVariables,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    skip: !playerId,
  });

  const isRefreshing = networkStatus === NetworkStatus.refetch;
  // NetworkStatus.setVariables (4) is when filter variables change
  // Only show full skeleton on initial load (networkStatus 1), not on filter changes
  const isInitialLoading = networkStatus === NetworkStatus.loading;

  const playerName = useMemo(
    () => playerData?.getPlayer?.name ?? "",
    [playerData?.getPlayer]
  );

  const items = useMemo(
    () =>
      (data?.listSimilarPlayers.edges ?? [])
        .map(e => e.node)
        .filter(item => !!item.player && item.player?.id !== playerId),
    [data?.listSimilarPlayers.edges, playerId]
  );

  // SE-7596: the same read-the-error fix SE-7580 landed for the Similar
  // Players TAB, lifted for this standalone screen. errorPolicy: "all"
  // (providers/ApolloClient.tsx) resolves a failed read with error set
  // rather than rejecting, so data === undefined + loading === false
  // was indistinguishable from a genuinely empty result -- the screen
  // asserted "we could not find any similar players" when the truth was
  // "we do not know". isPending covers every in-flight networkStatus
  // (via notifyOnNetworkStatusChange) plus the skipped-query window (no
  // playerId yet), matching the Tab queryLoading semantics.
  const isPending = !playerId || allDataLoading;

  const listState = useMemo<SimilarPlayersListState>(
    () =>
      deriveSimilarPlayersListState({
        players: items as SimilarPlayersPlayerResultFragment[],
        isPending,
        hasError: Boolean(similarPlayersError),
      }),
    [items, isPending, similarPlayersError]
  );

  const endCursor = useMemo(
    () =>
      data?.listSimilarPlayers?.pageInfo.hasNextPage
        ? data?.listSimilarPlayers?.pageInfo.endCursor
        : null,
    [
      data?.listSimilarPlayers?.pageInfo.endCursor,
      data?.listSimilarPlayers?.pageInfo.hasNextPage,
    ]
  );

  const handleReset = useCallback(() => {
    filterValuesVar(null);
    similaritySortVar(undefined);
  }, []);

  const handleRefresh = useCallback(() => {
    refetch(queryVariables).catch(() => {});
  }, [refetch, queryVariables]);

  // SE-7596: computed once handleRefresh exists so both the FlashList empty
  // slot and the stale banner retry the same way pull-to-refresh does.
  const listEmptyComponent = useMemo(
    () =>
      renderListEmptyComponent({
        listState,
        isRefreshing,
        onRetry: handleRefresh,
      }),
    [listState, isRefreshing, handleRefresh]
  );

  const staleBanner = useMemo(
    () => renderStaleBanner(listState, isRefreshing, handleRefresh),
    [listState, isRefreshing, handleRefresh]
  );

  const handleLoadMore = useCallback(() => {
    if (endCursor && !allDataLoading) {
      fetchMore({ variables: { ...queryVariables, after: endCursor } });
    }
  }, [endCursor, allDataLoading, fetchMore, queryVariables]);

  const handlePressAccordion = useCallback(() => {
    if (!valueAccordion.length) {
      setValueAccordion(["filter"]);
    } else {
      setValueAccordion([]);
    }
  }, [valueAccordion.length]);

  const handleCloseAccordion = useCallback(() => {
    if (valueAccordion.length) {
      setValueAccordion([]);
    }
  }, [valueAccordion.length]);

  // The route player's first load only: the poll's refetches (visible via
  // notifyOnNetworkStatusChange) keep the cached player on screen.
  const isRoutePlayerInitialLoading = playerLoading && !playerData?.getPlayer;

  const renderHeader = useCallback(
    () =>
      isRoutePlayerInitialLoading ? null : (
        <Header
          playerName={playerName}
          valueAccordion={valueAccordion}
          onPressAccordion={handlePressAccordion}
        />
      ),
    [
      handlePressAccordion,
      isRoutePlayerInitialLoading,
      playerName,
      valueAccordion,
    ]
  );

  // SE-8573: until a similar player is picked, the desktop centre pane shows
  // the ROUTE player — the one this container already polls for the header.
  // Handing it that result (PlayerDetailCenter's parent-owned mode, SE-5038)
  // keeps it from self-fetching the same id on a second 30s poller, which
  // doubled every PlayerDetailGetPlayer tick on this screen. Once a similar
  // player is selected both stay undefined and the pane fetches that player.
  const routePlayer = playerDetail ? undefined : playerData?.getPlayer;
  const routePlayerLoading = playerDetail
    ? undefined
    : isRoutePlayerInitialLoading;

  const handlePressSimilarPlayer = useCallback(
    (pDetail: PlayerSearchPlayerFragment | undefined) =>
      setPlayerDetail(pDetail),
    [setPlayerDetail]
  );

  const renderItem: ListRenderItem<SimilarPlayersPlayerResultFragment> =
    useCallback(
      ({ item }) => (
        <SearchResultsItem
          item={item?.player as PlayerSearchPlayerFragment}
          score={item?.similarityScore}
          goToSimilarPlayers={handleReset}
          onPressSimilarPlayer={handlePressSimilarPlayer}
        />
      ),
      [handlePressSimilarPlayer, handleReset]
    );

  const renderListFooterComponent = useCallback(
    () =>
      allDataLoading && !isRefreshing && items.length ? (
        <Spinner size="large" color="white" />
      ) : null,
    [allDataLoading, isRefreshing, items.length]
  );

  useSetPageTitle("Similar Players");

  // SE-4010: feature-scoped analytics -- carries the subject player_id the
  // generic route-level screen_view cannot express. Once per subject player.
  useEffect(() => {
    if (!playerId) return;
    void AnalyticsTracker.player.logSimilarPlayersViewed({
      player_id: playerId,
    });
  }, [playerId]);

  // Show full-screen skeleton only on initial load, not during filter changes
  if (isInitialLoading && items.length === 0) {
    return <LoadingSkeleton />;
  }

  return (
    <MainView
      loading={allDataLoading}
      refreshing={isRefreshing}
      isDark={isDark}
      listEmptyComponent={listEmptyComponent}
      staleBanner={staleBanner}
      similarPlayerResults={items as SimilarPlayersPlayerResultFragment[]}
      bottomInset={bottom + spacingForDevicesWithoutNotch}
      isSmallScreen={isSmallScreen}
      onLoadMore={handleLoadMore}
      onRefresh={handleRefresh}
      onScrollBeginDrag={handleCloseAccordion}
      renderItem={renderItem}
      renderHeader={renderHeader}
      renderListFooterComponent={renderListFooterComponent}
      playerDetail={playerDetail}
      routePlayer={routePlayer}
      routePlayerLoading={routePlayerLoading}
      onAddPlayer={handleAddPlayer}
      showActionSheet={showActionSheet}
      onClose={handleClose}
      promptRef={promptRef}
      onAddWatchlistPress={handleAddWatchlistPress}
    />
  );
};

export default MainContainer;
